// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import getCarouselData from 'app/common/react/components/common/Carousel/utils/getCarouselData';
import i18n from 'app/common/lib/i18n';
import ImpressionTracker from 'app/common/lib/react/ImpressionTracker';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import { Experiment } from 'app/common/react/components/experiments';
import { PRODUCT_PIN_HEIGHT_WIDTH_RATIO } from 'app/common/react/components/shopping/ProductPin/consts';
import type { CarouselPinData, CarouselSlotData } from 'app/packages/pinterest-flow-types/pin';
import { Box, Image, Text } from 'gestalt';

type ReduxStateProps = {|
  carouselData: ?CarouselPinData,
|};

type OwnProps = {|
  carouselData?: ?CarouselPinData,
  carouselIndex?: number,
  closeupHeight?: number,
  closeupWidth?: number,
  cropProductPinWidth?: number,
  highResOnly?: boolean,
  isCloseup?: boolean,
  isEditMode?: boolean,
  lowResOnly?: boolean,
  onError?: () => void,
  onLoad?: () => void,
  pinId: string,
  showCarouselBadge?: boolean,
|};

type Props = {|
  ...OwnProps,
  ...ReduxStateProps,
|};

type State = {|
  showBadge: boolean,
|};

// Ensure when doing high res carousel, giraffe pins don't overwhelm vertical space, PDS ratio
const HIGH_RES_MIN_ASPECT_RATIO = 4 / 5;
const HIGH_RES_WIDTH = 474;

const renderCarouselSlotImage = (
  slot: CarouselSlotData,
  pinId: string,
  slotIndex: number,
  highResOnly?: boolean,
  lowResOnly?: boolean,
  onError?: () => void,
  onLoad?: () => void,
  isCloseup?: boolean,
  closeupWidth?: number,
  closeupHeight?: number,
  cropCarouselProductPinWidth?: number,
) => {
  const { images, details = '' } = slot;
  let imageWidth;
  let imageHeight;
  let imgSrc;
  let srcSet;

  if (isCloseup) {
    // Pulled this scaling effect from CloseupBody.js
    imageWidth = closeupWidth || 0;
    imageHeight = closeupHeight || 0;
    // Carousel product pin may not have standard carousel image size.
    imgSrc =
      images &&
      ((images['564x'] && images['564x'].url) ||
        (images['736x'] && images['736x'].url) ||
        (images['236x'] && images['236x'].url));
  } else if (highResOnly) {
    imageWidth = HIGH_RES_WIDTH;
    imageHeight =
      images &&
      Math.min(
        imageWidth / HIGH_RES_MIN_ASPECT_RATIO,
        imageWidth / (images['564x'].width / images['564x'].height),
      );
    imgSrc = images && images['564x'].url;
  } else {
    imageHeight = images && images['236x'].height;
    imageWidth = images && images['236x'].width;
    imgSrc = images && images['236x'].url;
  }

  if (lowResOnly) {
    srcSet = `${images['236x'].url} 1x`;
  } else if (highResOnly) {
    srcSet = `${images['564x'].url} 1x`;
  } else {
    srcSet = [
      images['474x'] && `${images['474x'].url} 2x`,
      images['736x'] && `${images['736x'].url} 3x`,
      images.orig && `${images.orig.url} 4x`,
    ]
      .filter(Boolean)
      .join(', ');
  }

  const imageAlt =
    details ||
    `${i18n._(
      'carousel image',
      'Alternate text for a carousel image that indicates it is a carousel pin, with carousel index followed',
    )} ${slotIndex}`;

  return (
    <Box
      key={`carousel-slot-${pinId}-${slotIndex}`}
      {...(cropCarouselProductPinWidth ? {} : { height: imageHeight, width: imageWidth })}
    >
      <Box
        {...(cropCarouselProductPinWidth
          ? {
              height: cropCarouselProductPinWidth * PRODUCT_PIN_HEIGHT_WIDTH_RATIO,
              width: cropCarouselProductPinWidth,
            }
          : {})}
      >
        <Experiment name="web_image_loading_optimization">
          {({ group: imageLoadingOptimizationGroup }) => (
            <Image
              alt={imageAlt}
              fit={cropCarouselProductPinWidth ? 'cover' : 'none'}
              naturalHeight={imageHeight}
              naturalWidth={imageWidth}
              onError={onError}
              onLoad={onLoad}
              src={imgSrc}
              srcSet={srcSet}
              loading={imageLoadingOptimizationGroup.includes('lazy') ? 'lazy' : 'auto'}
              importance={imageLoadingOptimizationGroup.includes('low') ? 'low' : 'auto'}
            />
          )}
        </Experiment>
      </Box>
    </Box>
  );
};

export class Carousel extends Component<Props, State> {
  static defaultProps = {
    showCarouselBadge: true,
  };

  state = {
    showBadge: true,
  };

  componentDidMount() {
    // on load, remove badge after 3 seconds
    this.hideBadgeTimeout = setTimeout(() => this.setState({ showBadge: false }), 3000);
  }

  componentDidUpdate(prevProps: Props) {
    const { showBadge } = this.state;
    const { carouselData: prevCarouselData } = prevProps;
    const { carouselData: currCarouselData } = this.props;
    if (!prevCarouselData || !currCarouselData) {
      return;
    }
    const { index: prevIndex } = prevCarouselData;
    const { index: currIndex } = currCarouselData;
    if (showBadge) {
      if (this.hideBadgeTimeout) {
        clearTimeout(this.hideBadgeTimeout);
      }
      this.hideBadgeTimeout = setTimeout(() => this.setState({ showBadge: false }), 3000);
    }
    if (prevIndex !== currIndex) {
      // reset when user is interacting with the carousel
      if (this.hideBadgeTimeout) {
        clearTimeout(this.hideBadgeTimeout);
      }
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        showBadge: true,
      });
    }
  }

  componentWillUnmount() {
    if (this.hideBadgeTimeout) {
      clearTimeout(this.hideBadgeTimeout);
    }
  }

  hideBadgeTimeout: ?TimeoutID;

  renderBadge = () => {
    const { carouselData, isCloseup, showCarouselBadge } = this.props;
    if (!carouselData) {
      return null;
    }
    const { showBadge } = this.state;
    const { carousel_slots, index } = carouselData;
    const currentOpacity = showBadge ? 0.8 : 0;

    if (!isCloseup && showCarouselBadge) {
      // hide badge after 3 seconds
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          color="darkGray"
          position="absolute"
          right
          height={35}
          width={35}
          marginRight={5}
          marginTop={5}
          shape="rounded"
          dangerouslySetInlineStyle={{
            __style: {
              zIndex: 1,
              opacity: currentOpacity,
              transition: 'opacity .3s',
            },
          }}
        >
          <Text color="white" size="md">{`${index + 1}/${carousel_slots.length}`}</Text>
        </Box>
      );
    }

    return null;
  };

  render() {
    const {
      isCloseup,
      carouselIndex,
      closeupHeight,
      closeupWidth = 0,
      cropProductPinWidth,
      carouselData,
      highResOnly,
      lowResOnly,
      onError,
      onLoad,
      pinId,
      isEditMode,
    } = this.props;

    if (!carouselData) {
      return null;
    }

    const { carousel_slots = [], index } = carouselData;
    const carouselIndexFromParent = typeof carouselIndex !== 'undefined' ? carouselIndex : index;
    // Crop image for carousel product pinrep on closeup related products.
    const cropCarouselProductPinWidth = !isCloseup && cropProductPinWidth;

    let widthToUse;
    if (isCloseup) {
      widthToUse = closeupWidth;
    } else if (cropCarouselProductPinWidth) {
      widthToUse = cropCarouselProductPinWidth;
    } else if (highResOnly) {
      widthToUse = HIGH_RES_WIDTH;
    } else {
      widthToUse = 236;
    }

    return (
      <Box height={closeupHeight || '100%'} width={widthToUse} overflow="hidden">
        {!isEditMode && this.renderBadge()}
        {/* Not wrapping entire component bc remounting screws up sliding animation */}
        <ImpressionTracker
          trackCarousel
          key={`carousel-impression-key-${carouselIndexFromParent}`}
          impressionType="Pin"
          loggingId={pinId}
          slotIndex={index}
          viewParameter={72}
          viewType={1}
        >
          <Box
            position="absolute"
            height={closeupHeight || '100%'}
            width={widthToUse}
            overflow="hidden"
          />
        </ImpressionTracker>
        <Box
          width={widthToUse * carousel_slots.length}
          dangerouslySetInlineStyle={{
            __style: {
              left: carouselIndexFromParent * widthToUse * -1 + 'px',
              transition: 'left .3s',
            },
          }}
          left
          height="100%"
          display="flex"
          direction="row"
          position="relative"
        >
          {carousel_slots.map((slot: CarouselSlotData, slotIndex: number) =>
            renderCarouselSlotImage(
              slot,
              pinId,
              slotIndex,
              highResOnly,
              lowResOnly,
              onError,
              onLoad,
              isCloseup,
              closeupWidth,
              closeupHeight,
              cropProductPinWidth,
            ),
          )}
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = (
  { pins }: ReduxState,
  { carouselData: carouselDataProp, pinId }: OwnProps,
): ReduxStateProps => {
  const pin = pins[pinId];
  return {
    carouselData: carouselDataProp || getCarouselData(pin),
  };
};

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  () => ({}),
)(Carousel);
